import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import Loader from "../../components/common/Loader";
import ErrorState from "../../components/common/ErrorState";
import applicationStatusService from "../../services/applicationStatusService";
import { FILE_BASE_URL } from "../../services/api";
import { formatDate } from "../../utils/format";
import logo from "../../assets/logo.png";

const STATUS_LABEL = {
  pending: "Pending",
  in_review: "In review",
  shortlisted: "Shortlisted",
  waitlisted: "Waitlisted",
  rejected: "Not selected",
};

export default function StudentApplicationDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  function load() {
    setState("loading");
    applicationStatusService
      .myApplicationDetail(id)
      .then((res) => {
        setData(res.data);
        setState("ready");
      })
      .catch(() => setState("error"));
  }

  useEffect(load, [id]); // eslint-disable-line

  if (state === "loading") return <div className="pm-page"><Loader label="Loading application" /></div>;
  if (state === "error") return <div className="pm-page"><ErrorState onRetry={load} /></div>;

  return (
    <div className="pm-student-shell">
      <header className="pm-student-topbar">
        <Link to="/" className="pm-auth-brand">
          <img src={logo} alt="Positive Moves" className="pm-brand-logo" />
        </Link>
      </header>

      <div className="pm-page">
        <Link to="/student/dashboard" className="pm-back-link">← My applications</Link>

        <header className="pm-page__header">
          <div>
            <span className="pm-eyebrow">{data.university_name}</span>
            <h1>Your application</h1>
          </div>
        </header>

        <div className="pm-detail-grid">
          <div className="pm-card">
            <h3>Overview</h3>
            <dl className="pm-dl">
              <div><dt>Applied</dt><dd>{formatDate(data.applied_date)}</dd></div>
              <div><dt>Location</dt><dd>{data.location || "—"}</dd></div>
              <div><dt>Hiring type</dt><dd>{data.type_of_hire || "—"}</dd></div>
            </dl>
          </div>
          <div className="pm-card">
            <h3>Academics</h3>
            <dl className="pm-dl">
              <div><dt>10th CGPA</dt><dd>{data["10_percentage"] ?? "—"}</dd></div>
              <div><dt>12th CGPA</dt><dd>{data["12_percentage"] ?? "—"}</dd></div>
              <div><dt>Current CGPA</dt><dd>{data.current_cgpa ?? "—"}</dd></div>
              <div><dt>Undergrad CGPA</dt><dd>{data.undergrad_cgpa ?? "—"}</dd></div>
            </dl>
          </div>
          <div className="pm-card">
            <h3>Documents</h3>
            <div className="pm-doc-list">
              <a className={`pm-doc-link ${!data.cv ? "is-disabled" : ""}`} href={data.cv ? `${FILE_BASE_URL}/${data.cv}` : undefined} target="_blank" rel="noreferrer">
                📄 CV / Resume
              </a>
              <a className={`pm-doc-link ${!data.video ? "is-disabled" : ""}`} href={data.video ? `${FILE_BASE_URL}/${data.video}` : undefined} target="_blank" rel="noreferrer">
                🎬 Introduction video {data.video_scan_status === "pending" ? "(verifying…)" : ""}
              </a>
            </div>
          </div>
        </div>

        <div className="pm-card" style={{ marginTop: 20 }}>
          <h3>Your progress</h3>
          {!data.timeline?.length ? (
            <p className="pm-body-text">Rounds haven't been set up for this campus yet — you'll see your progress here as soon as they are.</p>
          ) : (
            <div className="pm-round-timeline">
              {data.timeline.map((step, i) => (
                <motion.div
                  className={`pm-round-step pm-round-step--${step.status || "upcoming"}`}
                  key={step.round_id}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.06 }}
                >
                  <span className="pm-round-step__dot" />
                  <div>
                    <strong>{step.round_name}</strong>
                    <span>{STATUS_LABEL[step.status] || "Upcoming"}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}